import { useMemo, useState, type Dispatch, type FormEvent, type SetStateAction } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, StatusDot } from '@diwata/ironvale'
import { useUI } from '@/store/ui'
import { ApiError } from '@/api/client'
import {
  DomainConflictError,
  DomainsAPI,
  domainSlug,
  type CatalogSpring,
  type CategoryEgoSuggestion,
  type Domain,
  type DomainCategory,
  type DomainDraft,
  type DomainTemplate,
  type EgoRelationKind,
  type EgoRelationSpec,
  type TemplatePreview,
} from '@/api/domains'
import { filterSpringsByTags, tagsOf } from '@/lib/springFilter'
import {
  allShelfTags,
  filterByTags,
  groupByShelf,
  groupCategoriesByShape,
  SPHERE_SUGGESTIONS,
  toTagSlug,
} from '@/lib/sphereShelves'
import { useDomainSelection } from '@/store/domainSelection'
import { DomainExplainer } from '@/components/DomainExplainer'
import { useOnboardingReentry } from '@/components/Onboarding'
import { HelpHint } from '@/components/HelpHint'
import { FlowDiagram } from '@/components/FlowDiagram'
import { TalaRefine } from '@/components/TalaRefine'
import { EVENT_GLOSS, STATE_GLOSS, mergeTypes, type TalaSuggestion } from '@/lib/talaRefine'
import './domains.css'

// The domain workbench — create, inspect, and edit the domains of your world.
// Cards are clickable → inspected inline here and in the control plane.

/** DomainDraft plus the sphere-tags key, sent optimistically — the API tolerates unknown
 *  keys, and the tags work lands the field on DomainDraft proper in parallel. Local so we
 *  don't touch api/domains.ts (another agent owns it). */
type DraftWithTags = DomainDraft & { tags?: string[] }

/** A template preview that MAY carry sphere tags — feature-detected, since the tags land
 *  server-side in parallel. Untagged payloads degrade to the flat gallery. */
type TaggedTemplate = TemplatePreview & { tags?: string[] }

/** Vocabulary words are lowercase_snake — "Board Member" becomes board_member. */
function toSnake(word: string): string {
  return word
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

/** Type a word, Enter adds a chip, click a chip to remove it. Chips normalize through
 *  `normalize` — lowercase_snake by default (vocabulary words), but the sphere-tags field
 *  passes `toTagSlug` because the backend's tag validator accepts kebab ONLY and rejects
 *  (never coerces) underscores — a snake_case tag would 422 the whole create. */
function ChipInput({
  chips,
  onChange,
  placeholder,
  normalize = toSnake,
}: {
  chips: string[]
  onChange: (next: string[]) => void
  placeholder: string
  normalize?: (word: string) => string
}) {
  const [draft, setDraft] = useState('')

  function add() {
    const chip = normalize(draft)
    if (!chip) return
    if (!chips.includes(chip)) onChange([...chips, chip])
    setDraft('')
  }

  return (
    <div className="dw-chipinput">
      {chips.map((chip) => (
        <button
          key={chip}
          type="button"
          className="dw-chip dw-chip--removable"
          title={`remove ${chip}`}
          onClick={() => onChange(chips.filter((c) => c !== chip))}
        >
          {chip}
          <span aria-hidden="true">×</span>
        </button>
      ))}
      <input
        className="dw-chipinput-field"
        value={draft}
        placeholder={chips.length === 0 ? placeholder : ''}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            add()
          }
        }}
      />
    </div>
  )
}

// The matcher only routes signals for types listed in routable_types — a domain
// with none NEVER gets mention rules, however many springs feed it. This picker
// is the one UI that sets it (the old custom flow's checkbox, restored): each
// chosen entity type gets a toggle. Old default was OFF, kept — but visible.
const ROUTABLE_MAX = 8
const ROUTABLE_GLOSS =
  "Signals mention-matching watches this type's names — airing episodes, mentions, releases route into this domain."

function RoutableTypesPicker({
  entityTypes,
  routable,
  onChange,
}: {
  entityTypes: string[]
  routable: string[]
  onChange: (next: string[]) => void
}) {
  if (entityTypes.length === 0) return null
  return (
    <div className="dw-form-field">
      <span>
        Route signals on these names <HelpHint gloss={ROUTABLE_GLOSS} />
      </span>
      <div className="dw-chip-row">
        {entityTypes.map((t) => {
          const on = routable.includes(t)
          const capped = !on && routable.length >= ROUTABLE_MAX
          return (
            <button
              key={t}
              type="button"
              className={`dw-chip${on ? ' dw-chip--removable' : ''}`}
              disabled={capped}
              aria-pressed={on}
              title={
                on
                  ? `${t} routes signals — click to stop`
                  : capped
                    ? `at most ${ROUTABLE_MAX} types can route signals`
                    : `route signals on ${t} names`
              }
              style={{ cursor: capped ? 'default' : 'pointer', opacity: capped ? 0.4 : 1 }}
              onClick={() => onChange(on ? routable.filter((x) => x !== t) : [...routable, t])}
            >
              <span aria-hidden="true">{on ? '◆ ' : '◇ '}</span>
              {t}
            </button>
          )
        })}
      </div>
      <span className="dw-field-hint">
        {routable.length === 0
          ? 'none routed — springs still fill the lake, but no signals fire for this domain'
          : `signals route on ${routable.length} of ${entityTypes.length} types`}
      </span>
    </div>
  )
}

// An ego relation row under edit. `extra` preserves backend fields beyond kind
// (template forks carry weight/group) so a rename-only edit doesn't drop them.
interface EgoRow {
  name: string
  kind: EgoRelationKind
  extra: Record<string, unknown>
}

function egoRowsFrom(domain?: Domain): EgoRow[] {
  return Object.entries(domain?.ego_relations ?? {}).map(([name, spec]) => {
    const { kind, ...extra } = spec
    return { name, kind: kind === 'state' ? 'state' : 'event', extra }
  })
}

/** Build the POST/PATCH ego_relations map from edited rows — blank names dropped. */
function egoMapFrom(rows: EgoRow[]): Record<string, EgoRelationSpec> {
  const ego: Record<string, EgoRelationSpec> = {}
  for (const row of rows) {
    const key = toSnake(row.name)
    if (key) ego[key] = { ...row.extra, kind: row.kind }
  }
  return ego
}

/** The one editor for Me → entity rows — shared by the guided builder and the manual form. */
function EgoRowsEditor({ rows, onChange }: { rows: EgoRow[]; onChange: (rows: EgoRow[]) => void }) {
  function setRow(i: number, patch: Partial<EgoRow>) {
    onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)))
  }
  return (
    <>
      <span className="dw-field-hint">
        <HelpHint gloss={EVENT_GLOSS}>
          <strong>event</strong>
        </HelpHint>{' '}
        = happened at a moment ·{' '}
        <HelpHint gloss={STATE_GLOSS}>
          <strong>state</strong>
        </HelpHint>{' '}
        = ongoing until you change it
      </span>
      {rows.map((row, i) => (
        <div key={i} className="dw-ego-row">
          <input
            className="dw-input"
            value={row.name}
            placeholder="e.g. attended, believes_in"
            onChange={(e) => setRow(i, { name: e.target.value })}
          />
          <select
            className="dw-input"
            value={row.kind}
            title={row.kind === 'state' ? STATE_GLOSS : EVENT_GLOSS}
            onChange={(e) => setRow(i, { kind: e.target.value as EgoRelationKind })}
          >
            <option value="event" title={EVENT_GLOSS}>event</option>
            <option value="state" title={STATE_GLOSS}>state</option>
          </select>
          <button
            type="button"
            className="dw-action-btn"
            title="remove ego relation"
            onClick={() => onChange(rows.filter((_, j) => j !== i))}
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        className="dw-action-btn"
        style={{ justifySelf: 'start' }}
        onClick={() => onChange([...rows, { name: '', kind: 'event', extra: {} }])}
      >
        + Add ego relation
      </button>
    </>
  )
}

/** Merge applied Tala suggestions into draft state — one implementation for the guided builder
 *  and the manual/edit form. Names arrive snake-normalized from the parser; mergeTypes dedupes;
 *  ego suggestions land as rows carrying the kind Tala marked. */
function applyDraftSuggestions(
  suggestions: TalaSuggestion[],
  setEntityTypes: Dispatch<SetStateAction<string[]>>,
  setRelationTypes: Dispatch<SetStateAction<string[]>>,
  setEgoRows: Dispatch<SetStateAction<EgoRow[]>>,
) {
  const ents = suggestions.filter((s) => s.kind === 'entity').map((s) => s.name)
  const edges = suggestions.filter((s) => s.kind === 'edge').map((s) => s.name)
  const egos = suggestions.filter((s) => s.kind === 'ego')
  if (ents.length > 0) setEntityTypes((cur) => mergeTypes(cur, ents))
  if (edges.length > 0) setRelationTypes((cur) => mergeTypes(cur, edges))
  if (egos.length > 0)
    setEgoRows((rows) => {
      const have = new Set(rows.map((r) => toSnake(r.name)))
      const fresh = egos.filter((e) => !have.has(e.name))
      return fresh.length > 0
        ? [...rows, ...fresh.map((e) => ({ name: e.name, kind: e.egoKind, extra: {} }))]
        : rows
    })
}

interface DomainFormProps {
  initial?: Domain
  submitLabel: string
  busy: boolean
  /** 409 detail — rendered under the name field. */
  nameError: string | null
  /** Anything else that went wrong — rendered by the actions. */
  formError: string | null
  onSubmit: (draft: DomainDraft) => void
  onCancel: () => void
}

/** One form for both create and edit — pass `initial` to pre-fill. */
function DomainForm({ initial, submitLabel, busy, nameError, formError, onSubmit, onCancel }: DomainFormProps) {
  const [name, setName] = useState(initial?.name ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [entityTypes, setEntityTypes] = useState<string[]>(initial ? [...initial.entity_types] : [])
  const [relationTypes, setRelationTypes] = useState<string[]>(initial ? [...initial.relation_types] : [])
  const [egoRows, setEgoRows] = useState<EgoRow[]>(() => egoRowsFrom(initial))
  const [routable, setRoutable] = useState<string[]>(initial ? [...(initial.routable_types ?? [])] : [])

  const valid = name.trim().length > 0 && entityTypes.length > 0

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!valid || busy) return
    onSubmit({
      name: name.trim(),
      description: description.trim(),
      entity_types: entityTypes,
      relation_types: relationTypes,
      ego_relations: egoMapFrom(egoRows),
      // a type removed above must not linger as a phantom routable type
      routable_types: routable.filter((t) => entityTypes.includes(t)),
    })
  }

  return (
    <form className="dw-domain-form" onSubmit={submit}>
      <label>
        <span>Name</span>
        <input
          className="dw-input"
          value={name}
          placeholder="e.g. Ateliers"
          onChange={(e) => setName(e.target.value)}
        />
        {nameError && <span className="dw-field-error">{nameError}</span>}
      </label>

      <label>
        <span>Description</span>
        <textarea
          className="dw-input dw-textarea"
          value={description}
          placeholder="What this domain of your world holds."
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>

      <div className="dw-form-field">
        <span>Entity types</span>
        <ChipInput chips={entityTypes} onChange={setEntityTypes} placeholder="type a word, Enter adds a chip" />
        {entityTypes.length === 0 && <span className="dw-field-hint">at least one entity type</span>}
      </div>

      <RoutableTypesPicker entityTypes={entityTypes} routable={routable} onChange={setRoutable} />

      <div className="dw-form-field">
        <span>Relation types</span>
        <ChipInput chips={relationTypes} onChange={setRelationTypes} placeholder="entity ↔ entity edges" />
      </div>

      <div className="dw-form-field">
        <span>Your relations — Me → entity</span>
        <EgoRowsEditor rows={egoRows} onChange={setEgoRows} />
      </div>

      <div className="dw-form-field">
        <TalaRefine
          surface="domains"
          domainName={name}
          entityTypes={entityTypes}
          relationTypes={relationTypes}
          egoNames={egoRows.map((r) => toSnake(r.name)).filter(Boolean)}
          onApply={(s) => applyDraftSuggestions(s, setEntityTypes, setRelationTypes, setEgoRows)}
          buttonStyle={{ justifySelf: 'start' }}
        />
      </div>

      <div className="dw-form-actions">
        {formError && <span className="dw-field-error">{formError}</span>}
        <button type="submit" className="dw-btn" disabled={!valid || busy}>
          {submitLabel}
        </button>
        <button type="button" className="dw-action-btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  )
}

/** A clickable suggestion chip — adds a word to a chip set, disabled once present. */
function SuggestChip({ word, chosen, onAdd }: { word: string; chosen: boolean; onAdd: () => void }) {
  return (
    <button
      type="button"
      className="dw-chip"
      disabled={chosen}
      title={chosen ? `${word} — already added` : `add ${word}`}
      onClick={onAdd}
      style={{ cursor: chosen ? 'default' : 'pointer', opacity: chosen ? 0.4 : 1 }}
    >
      {word}
      {!chosen && <span aria-hidden="true" style={{ marginLeft: 4, opacity: 0.6 }}>+</span>}
    </button>
  )
}

/** The HYBRID suggestions rail: the reliable curated floor (category → entity/edge types +
 *  attachable springs with liveness), plus Tala refine — advisory prose rendered as Markdown
 *  and a machine block the keeper APPLIES as chips instead of retyping (lib/talaRefine). */
function SuggestionRail({
  category,
  entityTypes,
  relationTypes,
  egoNames,
  name,
  onAddEntity,
  onAddRelation,
  onAddEgo,
  onApplySuggestions,
}: {
  category: DomainCategory | undefined
  entityTypes: string[]
  relationTypes: string[]
  egoNames: string[]
  name: string
  onAddEntity: (t: string) => void
  onAddRelation: (t: string) => void
  onAddEgo: (e: CategoryEgoSuggestion) => void
  onApplySuggestions: (suggestions: TalaSuggestion[]) => void
}) {
  const [tags, setTags] = useState<string[]>([])

  const springs = category?.springs ?? []
  const allTags = useMemo(() => tagsOf(springs), [springs])
  const shownSprings = useMemo(() => filterSpringsByTags(springs, tags), [springs, tags])

  function toggleTag(t: string) {
    setTags((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]))
  }

  if (!category) return null

  return (
    <div className="dw-builder-rail">
      <div className="dw-rail-section-title">Suggested for {category.label}</div>

      <div className="dw-field-label" style={{ marginBottom: 4 }}>Entity types</div>
      <div className="dw-chip-row" style={{ marginBottom: 10 }}>
        {category.entity_types.map((t) => (
          <SuggestChip key={t} word={t} chosen={entityTypes.includes(t)} onAdd={() => onAddEntity(t)} />
        ))}
        {category.entity_types.length === 0 && <span className="dw-rail-empty">none curated yet</span>}
      </div>

      <div className="dw-field-label" style={{ marginBottom: 4 }}>Edge types</div>
      <div className="dw-chip-row" style={{ marginBottom: 10 }}>
        {category.edge_types.map((t) => (
          <SuggestChip key={t} word={t} chosen={relationTypes.includes(t)} onAdd={() => onAddRelation(t)} />
        ))}
        {category.edge_types.length === 0 && <span className="dw-rail-empty">none curated yet</span>}
      </div>

      <div className="dw-field-label" style={{ marginBottom: 4 }}>Your relations</div>
      <div className="dw-chip-row" style={{ marginBottom: 10 }}>
        {(category.ego_relations ?? []).map((e) => (
          <button
            key={e.name}
            type="button"
            className="dw-chip"
            disabled={egoNames.includes(e.name)}
            title={egoNames.includes(e.name) ? `${e.name} — already added` : `${e.gloss} (${e.kind === 'state' ? 'ongoing' : 'happened once'})`}
            onClick={() => onAddEgo(e)}
            style={{ cursor: egoNames.includes(e.name) ? 'default' : 'pointer', opacity: egoNames.includes(e.name) ? 0.4 : 1 }}
          >
            {e.name}
            {!egoNames.includes(e.name) && <span aria-hidden="true" style={{ marginLeft: 4, opacity: 0.6 }}>+</span>}
          </button>
        ))}
        {(category.ego_relations ?? []).length === 0 && <span className="dw-rail-empty">none curated yet</span>}
      </div>

      <div className="dw-field-label" style={{ marginBottom: 4 }}>
        Springs that can feed this domain
      </div>
      {allTags.length > 0 && (
        <div className="dw-chip-row" style={{ marginBottom: 6 }}>
          {allTags.map((t) => (
            <button
              key={t}
              type="button"
              className={`dw-chip${tags.includes(t) ? ' dw-chip--removable' : ''}`}
              style={{ cursor: 'pointer', fontSize: 'calc(10px * var(--ae-text-scale, 1))' }}
              onClick={() => toggleTag(t)}
              title={tags.includes(t) ? `clear ${t} filter` : `filter by ${t}`}
            >
              {t}
              {tags.includes(t) && <span aria-hidden="true"> ×</span>}
            </button>
          ))}
        </div>
      )}
      <div style={{ display: 'grid', gap: 4, marginBottom: 10 }}>
        {shownSprings.map((sp: CatalogSpring) => (
          <div key={sp.name} style={{ fontSize: 'var(--ae-font-size-xs)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <StatusDot
              state={sp.liveness}
              size="sm"
              label={`${sp.name} — ${sp.liveness}${sp.last_run ? `, last ${new Date(sp.last_run).toLocaleString()}` : ''}`}
            />
            <span>{sp.name}</span>
            <span className="dw-inbox-detail" style={{ fontSize: 'calc(10px * var(--ae-text-scale, 1))' }}>{sp.tags.join(' · ')}</span>
          </div>
        ))}
        {shownSprings.length === 0 && (
          <span className="dw-rail-empty">
            {springs.length === 0 ? 'no springs for this category — evidence arrives by your hand' : 'no springs match those tags'}
          </span>
        )}
      </div>
      <div className="dw-inbox-detail" style={{ fontSize: 'calc(10px * var(--ae-text-scale, 1))', marginBottom: 10 }}>
        Create the domain first, then attach any of these on its inspect panel.
      </div>

      <TalaRefine
        surface="domains"
        domainName={name}
        entityTypes={entityTypes}
        relationTypes={relationTypes}
        egoNames={egoNames}
        onApply={onApplySuggestions}
        buttonStyle={{ width: '100%' }}
      />
    </div>
  )
}

interface BuilderProps {
  submitLabel: string
  busy: boolean
  nameError: string | null
  formError: string | null
  onSubmit: (draft: DomainDraft) => void
  onCancel: () => void
}

/** THE create flow — everywhere. The category-driven guided builder with the hybrid
 *  suggestions rail beside the fields (page CTA and the control-plane create tab both
 *  render this). "Custom domain (advanced / manual)" is the only surviving form of the
 *  old blank-vocabulary path. */
export function DomainBuilder({ submitLabel, busy, nameError, formError, onSubmit, onCancel }: BuilderProps) {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new DomainsAPI(diwaUrl), [diwaUrl])
  const { data: catalog } = useQuery({ queryKey: ['domain-catalog'], queryFn: () => api.catalog() })
  const categories = catalog?.categories ?? []

  const [mode, setMode] = useState<'guided' | 'manual'>('guided')
  const [categoryKey, setCategoryKey] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [entityTypes, setEntityTypes] = useState<string[]>([])
  const [relationTypes, setRelationTypes] = useState<string[]>([])
  const [egoRows, setEgoRows] = useState<EgoRow[]>([{ name: 'note', kind: 'event', extra: {} }])
  const [routable, setRoutable] = useState<string[]>([])
  const [sphereTags, setSphereTags] = useState<string[]>([])

  const category = categories.find((c) => c.key === categoryKey) ?? categories[0]

  // Shape-aware picker, feature-detected: once the catalog stamps categories with shapes
  // (landing in parallel), the chips group under shape headings; a shapeless payload yields
  // one null group and the picker renders flat, exactly as before.
  const shapeGroups = useMemo(() => groupCategoriesByShape(categories), [categories])

  function addUnique(list: string[], word: string, set: (n: string[]) => void, normalize = toSnake) {
    const chip = normalize(word)
    if (chip && !list.includes(chip)) set([...list, chip])
  }

  function addEgo(e: CategoryEgoSuggestion) {
    setEgoRows((rows) =>
      rows.some((r) => toSnake(r.name) === e.name)
        ? rows
        : [...rows, { name: e.name, kind: e.kind, extra: e.group ? { group: e.group } : {} }],
    )
  }

  const applySuggestions = (s: TalaSuggestion[]) =>
    applyDraftSuggestions(s, setEntityTypes, setRelationTypes, setEgoRows)

  const valid = name.trim().length > 0 && entityTypes.length > 0

  function submitGuided() {
    if (!valid || busy) return
    const draft: DraftWithTags = {
      name: name.trim(),
      description: description.trim(),
      entity_types: entityTypes,
      relation_types: relationTypes,
      ego_relations: egoMapFrom(egoRows),
      // a type removed from the list must not linger as a phantom routable type
      routable_types: routable.filter((t) => entityTypes.includes(t)),
    }
    // Sent optimistically — the backend tolerates the key today and types it tomorrow.
    if (sphereTags.length > 0) draft.tags = sphereTags
    onSubmit(draft)
  }

  if (mode === 'manual') {
    return (
      <>
        <DomainExplainer />
        <div className="dw-builder-modes">
          <button type="button" className="dw-inline-link" onClick={() => setMode('guided')}>← guided builder</button>
          <span className="dw-inbox-detail">Custom domain: author the whole vocabulary yourself.</span>
        </div>
        <DomainForm
          submitLabel={submitLabel}
          busy={busy}
          nameError={nameError}
          formError={formError}
          onSubmit={onSubmit}
          onCancel={onCancel}
        />
      </>
    )
  }

  return (
    <div>
      <DomainExplainer />
      <div className="dw-builder-modes">
        <span className="dw-inbox-detail">Pick a category — the rail suggests what usually lives there.</span>
        <button type="button" className="dw-inline-link" onClick={() => setMode('manual')}>Custom domain (advanced / manual) →</button>
      </div>

      <div style={{ marginBottom: 14 }}>
        {shapeGroups.map((group) => (
          <div key={group.shape ?? '(flat)'} className={group.shape !== null || shapeGroups.length > 1 ? 'dw-shape-group' : undefined}>
            {(group.shape !== null || shapeGroups.length > 1) && (
              <div className="dw-field-label dw-shape-label">{group.shape ?? 'other'}</div>
            )}
            <div className="dw-chip-row">
              {group.items.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  className={`dw-chip${category?.key === c.key ? ' dw-chip--removable' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setCategoryKey(c.key)}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        ))}
        {categories.length === 0 && <span className="dw-rail-empty">loading categories…</span>}
      </div>

      <div className="dw-builder-grid">
        <div className="dw-builder-fields">
          <label>
            <span>Name</span>
            <input className="dw-input" value={name} placeholder="e.g. My watchlist" onChange={(e) => setName(e.target.value)} />
            {nameError && <span className="dw-field-error">{nameError}</span>}
          </label>
          <label>
            <span>Description</span>
            <textarea
              className="dw-input dw-textarea"
              value={description}
              placeholder="What this domain of your world holds."
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
          <div className="dw-form-field">
            <span>Sphere tags</span>
            <span className="dw-field-hint">which sphere of your life this domain lives in — shelves and filters use these</span>
            <ChipInput chips={sphereTags} onChange={setSphereTags} placeholder="type a tag, Enter adds it" normalize={toTagSlug} />
            <div className="dw-chip-row">
              {SPHERE_SUGGESTIONS.map((t) => (
                <SuggestChip
                  key={t}
                  word={t}
                  chosen={sphereTags.includes(t)}
                  onAdd={() => addUnique(sphereTags, t, setSphereTags, toTagSlug)}
                />
              ))}
            </div>
          </div>
          <div className="dw-form-field">
            <span>Entity types</span>
            <ChipInput chips={entityTypes} onChange={setEntityTypes} placeholder="type a word, or tap a suggestion →" />
            {entityTypes.length === 0 && <span className="dw-field-hint">at least one entity type</span>}
          </div>
          <RoutableTypesPicker entityTypes={entityTypes} routable={routable} onChange={setRoutable} />
          <div className="dw-form-field">
            <span>Relation types</span>
            <ChipInput chips={relationTypes} onChange={setRelationTypes} placeholder="entity ↔ entity edges" />
          </div>
          <div className="dw-form-field">
            <span>Your relations — Me → entity</span>
            <span className="dw-field-hint">how this domain records what YOU did — met, reading, rated…</span>
            <EgoRowsEditor rows={egoRows} onChange={setEgoRows} />
          </div>
          <div className="dw-form-actions">
            {formError && <span className="dw-field-error">{formError}</span>}
            <button type="button" className="dw-btn" disabled={!valid || busy} onClick={submitGuided}>
              {submitLabel}
            </button>
            <button type="button" className="dw-action-btn" onClick={onCancel}>Cancel</button>
          </div>
        </div>

        <SuggestionRail
          category={category}
          entityTypes={entityTypes}
          relationTypes={relationTypes}
          egoNames={egoRows.map((r) => toSnake(r.name)).filter(Boolean)}
          name={name}
          onAddEntity={(t) => addUnique(entityTypes, t, setEntityTypes)}
          onAddRelation={(t) => addUnique(relationTypes, t, setRelationTypes)}
          onAddEgo={addEgo}
          onApplySuggestions={applySuggestions}
        />
      </div>
    </div>
  )
}

function countsLabel(counts: { entities: number; relations: number }): string {
  const e = `${counts.entities} ${counts.entities === 1 ? 'entity' : 'entities'}`
  const r = `${counts.relations} ${counts.relations === 1 ? 'relation' : 'relations'}`
  return `${e} · ${r}`
}

export function Domains() {
  const { diwaUrl } = useUI()
  const api = new DomainsAPI(diwaUrl)
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['domains'], queryFn: () => api.list() })
  const { data: previewData } = useQuery({ queryKey: ['template-previews'], queryFn: () => api.templatePreviews() })
  const { selected, select } = useDomainSelection()
  const reenterOnboarding = useOnboardingReentry()

  // Home's "+ New domain" arrives as /domains?create=1 — land with the builder open.
  const [searchParams] = useSearchParams()
  const [showCreate, setShowCreate] = useState(() => searchParams.get('create') === '1')
  const [createNameError, setCreateNameError] = useState<string | null>(null)
  const [createFormError, setCreateFormError] = useState<string | null>(null)

  const [openId, setOpenId] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)
  const [editNameError, setEditNameError] = useState<string | null>(null)
  const [editFormError, setEditFormError] = useState<string | null>(null)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)

  const [busy, setBusy] = useState(false)
  const [templateBusy, setTemplateBusy] = useState<string | null>(null)
  const [templateError, setTemplateError] = useState<string | null>(null)

  const domains = (data?.domains ?? []).filter((d) => !d.shared)
  const joined = (data?.domains ?? []).filter((d) => d.shared)
  const openDomain = [...domains, ...joined].find((d) => d.id === openId)
  const ownedSlugs = new Set(domains.map((d) => d.slug))

  // Sphere shelves: templates carry tags from either endpoint (feature-detected — the tags
  // land server-side in parallel). Previews win; the /domains template list backfills.
  const [shelfFilter, setShelfFilter] = useState<string[]>([])
  const listTags = new Map(
    (data?.templates ?? []).map((t: DomainTemplate & { tags?: string[] }) => [t.key, t.tags]),
  )
  const templates: TaggedTemplate[] = (previewData?.templates ?? []).map((t: TaggedTemplate) =>
    t.tags?.length ? t : { ...t, tags: listTags.get(t.key) ?? undefined },
  )
  const shelfTags = allShelfTags(templates)
  const shownTemplates = filterByTags(templates, shelfFilter)
  const shelves = groupByShelf(shownTemplates)

  const { data: detail } = useQuery({
    queryKey: ['domain-detail', openId],
    queryFn: () => api.detail(openId!),
    enabled: !!openId,
  })

  function openCard(domain: Domain) {
    select(domain)
    setOpenId((prev) => (prev === domain.id ? null : domain.id))
    setEditing(false)
    setConfirmingDelete(false)
    setEditNameError(null)
    setEditFormError(null)
    setDetailError(null)
  }

  async function createDomain(draft: DomainDraft) {
    setBusy(true)
    setCreateNameError(null)
    setCreateFormError(null)
    try {
      await api.create(draft)
      await qc.invalidateQueries({ queryKey: ['domains'] })
      setShowCreate(false)
    } catch (err) {
      if (err instanceof DomainConflictError) setCreateNameError(err.detail)
      else if (err instanceof ApiError && err.status === 402) setCreateFormError(err.detail ?? 'Upgrade your plan to add more domains.')
      else setCreateFormError("Couldn't create the domain. Try again.")
    } finally {
      setBusy(false)
    }
  }

  async function saveEdit(draft: DomainDraft) {
    if (!openDomain) return
    setBusy(true)
    setEditNameError(null)
    setEditFormError(null)
    try {
      const updated = await api.update(openDomain.id, draft)
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['domains'] }),
        qc.invalidateQueries({ queryKey: ['domain-detail', openDomain.id] }),
      ])
      if (selected?.id === updated.id) select(updated) // keep the control plane fresh
      setEditing(false)
    } catch (err) {
      if (err instanceof ApiError && err.status === 409)
        setEditNameError('That name is already taken by another of your domains.')
      else setEditFormError("Couldn't save changes. Try again.")
    } finally {
      setBusy(false)
    }
  }

  async function deleteDomain() {
    if (!openDomain) return
    setBusy(true)
    setDetailError(null)
    try {
      await api.remove(openDomain.id)
      await qc.invalidateQueries({ queryKey: ['domains'] })
      if (selected?.id === openDomain.id) select(null)
      setOpenId(null)
      setConfirmingDelete(false)
    } catch {
      setDetailError("Couldn't delete the domain. Try again.")
    } finally {
      setBusy(false)
    }
  }

  async function applyTemplate(key: string) {
    setTemplateBusy(key)
    setTemplateError(null)
    try {
      await api.create({ template: key })
      await qc.invalidateQueries({ queryKey: ['domains'] })
    } catch (err) {
      if (err instanceof DomainConflictError) {
        setTemplateError(err.detail)
        await qc.invalidateQueries({ queryKey: ['domains'] }) // the fresh list disables the button
      } else {
        setTemplateError("Couldn't fork the template. Try again.")
      }
    } finally {
      setTemplateBusy(null)
    }
  }

  function DomainCard({ domain, meta }: { domain: Domain; meta: string }) {
    const isSel = selected?.slug === domain.slug
    const isArchived = domain.status === 'archived'
    return (
      <Card className="dw-card-stack">
        <div
          className="dw-row-clickable dw-card-stack-body"
          onClick={() => openCard(domain)}
          style={{
            outline: isSel ? '1px solid var(--ae-color-accent)' : 'none',
            outlineOffset: 8,
            borderRadius: 8,
            opacity: isArchived ? 0.5 : 1,
          }}
        >
          <div style={{ fontWeight: 600, letterSpacing: '0.03em', marginBottom: 6 }}>
            {/* Link-through to the domain's own page. A real <Link> (keyboard-focusable, honest
                href); stopPropagation keeps a name click from also toggling the inline inspect. */}
            <Link
              to={`/domains/${domain.slug}`}
              className="dw-domain-card-link"
              title={`open ${domain.name}'s page`}
              onClick={(e) => e.stopPropagation()}
            >
              {domain.name}
            </Link>
            {isArchived && <span className="dw-inbox-detail" style={{ marginLeft: 6, fontSize: 'calc(11px * var(--ae-text-scale, 1))' }}>archived</span>}
            {domain.shared && <span className="dw-inbox-detail" style={{ marginLeft: 6, fontSize: 'calc(11px * var(--ae-text-scale, 1))' }}>shared · {domain.owner} · you: {domain.role}</span>}
          </div>
          <div style={{ fontSize: 'var(--ae-font-size-xs)', color: 'var(--ae-color-text-secondary)', marginBottom: 8 }}>{domain.description || '—'}</div>
          <div
            className="dw-card-footer"
            style={{
              fontSize: 'calc(11px * var(--ae-text-scale, 1))',
              color: 'var(--ae-color-text-muted)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <span>{meta}</span>
            {/* The explicit door to the domain's own page — the name-link stays, but a visible
                affordance means nobody has to guess the name is clickable. */}
            <Link
              to={`/domains/${domain.slug}`}
              className="dw-inline-link"
              style={{ whiteSpace: 'nowrap' }}
              title={`open ${domain.name}'s page`}
              onClick={(e) => e.stopPropagation()}
            >
              Open page →
            </Link>
          </div>
        </div>
      </Card>
    )
  }

  const egoEntries = Object.entries(openDomain?.ego_relations ?? {})

  return (
    <div>
      <div className="dw-page-header" data-tour="domains-header">
        <div>
          <div className="dw-page-title">
            Domains <HelpHint term="domain" /> <FlowDiagram />
          </div>
          <div className="dw-page-subtitle">One substrate, many domains — click a domain to inspect it, or shape a new one.</div>
        </div>
        <button
          className="dw-btn"
          data-tour="domains-new"
          onClick={() => {
            setShowCreate((v) => !v)
            setCreateNameError(null)
            setCreateFormError(null)
          }}
        >
          {showCreate ? 'Close' : '+ New domain'}
        </button>
      </div>

      {showCreate && (
        <Card style={{ marginBottom: 18 }}>
          <div className="dw-rail-section-title">New domain</div>
          <DomainBuilder
            submitLabel="Create domain"
            busy={busy}
            nameError={createNameError}
            formError={createFormError}
            onSubmit={createDomain}
            onCancel={() => setShowCreate(false)}
          />
        </Card>
      )}

      {domains.length > 0 ? (
        <div data-tour="domains-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12, marginBottom: 18 }}>
          {domains.map((d) => (
            <DomainCard key={d.id} domain={d} meta={`${d.type} · ${d.entity_types.length} entity types · ${d.relation_types.length} relations`} />
          ))}
        </div>
      ) : (
        <div className="dw-rail-empty" style={{ marginBottom: 18 }} data-tour="domains-list">
          No domains yet — create one above, fork a template below, or{' '}
          <button type="button" className="dw-inline-link" onClick={reenterOnboarding}>
            ✦ Start again with Tala
          </button>
        </div>
      )}

      {joined.length > 0 && (
        <>
          <div className="dw-rail-section-title" style={{ marginBottom: 8 }}>Shared with you</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12, marginBottom: 18 }}>
            {joined.map((d) => (
              <DomainCard key={d.id} domain={d} meta={`${d.type} · ${d.entity_types.length} entity types · ${d.relation_types.length} relations`} />
            ))}
          </div>
        </>
      )}

      {openDomain && (
        <Card className="dw-accent" style={{ marginBottom: 24 }}>
          {editing ? (
            <>
              <div className="dw-rail-section-title">Edit domain</div>
              <DomainForm
                key={openDomain.id}
                initial={openDomain}
                submitLabel="Save changes"
                busy={busy}
                nameError={editNameError}
                formError={editFormError}
                onSubmit={saveEdit}
                onCancel={() => {
                  setEditing(false)
                  setEditNameError(null)
                  setEditFormError(null)
                }}
              />
            </>
          ) : (
            <>
              <div className="dw-domain-detail-head">
                <div>
                  <div style={{ fontWeight: 600, fontSize: 'var(--ae-font-size-sm)', letterSpacing: '0.03em' }}>{openDomain.name}</div>
                  <div className="dw-domain-counts">
                    {detail && detail.id === openDomain.id ? countsLabel(detail.counts) : '…'}
                  </div>
                </div>
                {openDomain.shared ? (
                  <span className="dw-inbox-detail">shared by {openDomain.owner} · you: {openDomain.role}</span>
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="dw-action-btn" onClick={() => setEditing(true)}>Edit</button>
                    {!confirmingDelete && (
                      <button className="dw-action-btn" onClick={() => setConfirmingDelete(true)}>Delete</button>
                    )}
                  </div>
                )}
              </div>

              {openDomain.description && (
                <div style={{ fontSize: 'var(--ae-font-size-xs)', color: 'var(--ae-color-text-secondary)', lineHeight: 1.45, marginBottom: 14 }}>
                  {openDomain.description}
                </div>
              )}

              <div className="dw-rail-section-title">Entity types</div>
              <div className="dw-chip-row" style={{ marginBottom: 12 }}>
                {openDomain.entity_types.map((t) => {
                  const routes = (openDomain.routable_types ?? []).includes(t)
                  return routes ? (
                    <HelpHint key={t} gloss={ROUTABLE_GLOSS}>
                      <span className="dw-chip"><span aria-hidden="true">◆ </span>{t}</span>
                    </HelpHint>
                  ) : (
                    <span key={t} className="dw-chip">{t}</span>
                  )
                })}
                {openDomain.entity_types.length === 0 && <span className="dw-rail-empty">none</span>}
              </div>

              <div className="dw-rail-section-title">Relation types</div>
              <div className="dw-chip-row" style={{ marginBottom: 12 }}>
                {openDomain.relation_types.map((t) => (
                  <span key={t} className="dw-chip">{t}</span>
                ))}
                {openDomain.relation_types.length === 0 && <span className="dw-rail-empty">none</span>}
              </div>

              <div className="dw-rail-section-title">Ego relations — Me → entity</div>
              <div className="dw-chip-row">
                {egoEntries.map(([name, spec]) => (
                  <span key={name} className="dw-chip">{name} · {spec.kind}</span>
                ))}
                {egoEntries.length === 0 && (
                  <span className="dw-rail-empty">none — this domain records nothing about you yet</span>
                )}
              </div>

              {confirmingDelete && (
                <div className="dw-domain-confirm">
                  <div style={{ fontSize: 'var(--ae-font-size-xs)', color: 'var(--ae-color-text-secondary)', lineHeight: 1.45 }}>
                    Delete “{openDomain.name}”? This does not delete the domain's entities — they keep their domain tag.
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="dw-btn dw-btn--danger" disabled={busy} onClick={deleteDomain}>
                      Delete domain
                    </button>
                    <button className="dw-action-btn" disabled={busy} onClick={() => setConfirmingDelete(false)}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {detailError && <div className="dw-error">{detailError}</div>}
            </>
          )}
        </Card>
      )}

      <div className="dw-rail-section-title" style={{ marginBottom: 10 }} data-tour="domains-templates">Diwa templates</div>

      {/* Tag-filter chips — ALL tags across templates, so a two-tag template is reachable
          through its second sphere even though it shelves under its first. */}
      {shelfTags.length > 0 && (
        <div className="dw-chip-row" style={{ marginBottom: 12 }}>
          {shelfTags.map((t) => (
            <button
              key={t}
              type="button"
              className={`dw-chip${shelfFilter.includes(t) ? ' dw-chip--removable' : ''}`}
              style={{ cursor: 'pointer' }}
              aria-pressed={shelfFilter.includes(t)}
              title={shelfFilter.includes(t) ? `clear ${t} filter` : `filter templates by ${t}`}
              onClick={() =>
                setShelfFilter((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]))
              }
            >
              {t}
              {shelfFilter.includes(t) && <span aria-hidden="true"> ×</span>}
            </button>
          ))}
        </div>
      )}

      {shelves.map((shelf) => (
        <div key={shelf.tag ?? '(untagged)'} className="dw-shelf">
          {/* An untagged payload yields one label-less shelf — today's flat grid, unchanged. */}
          {shelf.label && <div className="dw-field-label dw-shelf-label">{shelf.label}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
            {shelf.items.map((t) => (
              <TemplateCard key={t.key} t={t} />
            ))}
          </div>
        </div>
      ))}
      {templates.length > 0 && shownTemplates.length === 0 && (
        <div className="dw-rail-empty">no templates carry those tags — clear a chip above</div>
      )}
      {/* Suggest-a-template: v1 is a prefilled request; the Commune arc upgrades this
          into the community suggest → vote → build pipeline (see commune arc notes). */}
      <div className="dw-inbox-detail" style={{ marginTop: 4 }}>
        Don't see your world?{' '}
        <a
          className="dw-inline-link"
          href={
            'mailto:ss@diwata.domains?subject=Template%20request&body=' +
            encodeURIComponent(
              'The world I want to track:\n\nWhat lives in it (the things):\n\nWhat I want Diwa to do with it:\n',
            )
          }
        >
          Request a template →
        </a>
      </div>
      {templateError && <div className="dw-error">{templateError}</div>}
    </div>
  )

  /** One template in the gallery. Collapsed by default — name + description + sphere chips —
   *  because a wall of full entity/edge/ego previews drowns the shelf (founder note); the
   *  "what's inside" disclosure reveals the vocabulary. "Use template" stays pinned to the
   *  footer in BOTH states, so acting never requires expanding. */
  function TemplateCard({ t }: { t: TaggedTemplate }) {
    const owned = ownedSlugs.has(domainSlug(t.key))
    const [expanded, setExpanded] = useState(false)
    const detailsId = `dw-template-inside-${t.key}`
    return (
      <Card className="dw-card-stack">
              <div style={{ fontWeight: 600, marginBottom: 6, letterSpacing: '0.03em' }}>{t.name}</div>
              <div style={{ fontSize: 'var(--ae-font-size-xs)', color: 'var(--ae-color-text-secondary)', marginBottom: 10 }}>{t.description}</div>

              {(t.tags ?? []).length > 0 && (
                <div className="dw-chip-row" style={{ marginBottom: 10 }}>
                  {(t.tags ?? []).map((tag) => (
                    <span key={tag} className="dw-chip">{tag}</span>
                  ))}
                </div>
              )}

              <button
                type="button"
                className="dw-template-toggle"
                aria-expanded={expanded}
                aria-controls={detailsId}
                onClick={() => setExpanded((v) => !v)}
              >
                <span aria-hidden="true">{expanded ? '▾' : '▸'}</span> what's inside
              </button>

              {expanded && (
                <div id={detailsId}>
                  <div className="dw-field-label" style={{ marginBottom: 4 }}>Lives here</div>
                  <div style={{ display: 'grid', gap: 2, fontSize: 'var(--ae-font-size-xs)', marginBottom: 10 }}>
                    {t.entity_types.map((ty) => (
                      <div key={ty}>
                        <strong>{ty}</strong>
                        <span className="dw-inbox-detail"> — {t.words[ty]}</span>
                      </div>
                    ))}
                  </div>

                  <div className="dw-field-label" style={{ marginBottom: 4 }}>How they relate</div>
                  <div className="dw-chip-row" style={{ marginBottom: 10 }}>
                    {t.relation_types.map((r) => (
                      <span key={r} className="dw-chip">{r.replace(/_/g, ' ')}</span>
                    ))}
                    {t.relation_types.length === 0 && <span className="dw-rail-empty">no edges declared</span>}
                  </div>

                  <div className="dw-field-label" style={{ marginBottom: 4 }}>Your relations</div>
                  <div style={{ display: 'grid', gap: 2, fontSize: 'var(--ae-font-size-xs)', marginBottom: 10 }}>
                    {t.ego_relations.map((e) => (
                      <div key={e.name}>
                        <strong>{e.name.replace(/_/g, ' ')}</strong>
                        <span className="dw-inbox-detail"> — {e.word}</span>
                      </div>
                    ))}
                    {t.ego_relations.length === 0 && (
                      <span className="dw-rail-empty">none — this template records nothing about you</span>
                    )}
                  </div>
                </div>
              )}

              <div className="dw-card-footer">
                {owned ? (
                  <button className="dw-btn" disabled>already in your world</button>
                ) : (
                  <button className="dw-btn" disabled={templateBusy === t.key} onClick={() => applyTemplate(t.key)}>
                    Use template
                  </button>
                )}
              </div>
            </Card>
    )
  }
}
